import { z } from 'zod';

const PhoneNumberVariantSchema = z.enum(['hidden', 'required', 'optional']);

const CountryCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'Country code must be ISO 3166-1 alpha-2 uppercase');

const MeetingLinkSchema = z.union([
  z.string().url(),
  z.record(z.string(), z.string().url()),
]);

// `hidden` is a per-country kill switch: it suppresses the onboarding demo/
// webinar CTA entirely for that market, bypassing the universal `request-demo`
// fallback in `resolveOnboardingExperience`. Set it as a country's `primary`
// (e.g. `ES: { onboardingCta: 'hidden' }`) when there is no SDR/webinar cover
// for that market and showing any CTA would strand the user with a dead end.
const CtaSelectorSchema = z.enum(['meeting', 'request-demo', 'webinar', 'hidden']);

const CtaConfigObjectSchema = z
  .object({
    primary: CtaSelectorSchema,
    fallback: CtaSelectorSchema.optional(),
  })
  .strict();

const CtaConfigSchema = z.union([CtaSelectorSchema, CtaConfigObjectSchema]);

const OnboardingCtaSchema = z.union([
  CtaConfigSchema,
  z.record(z.string(), CtaConfigSchema),
]);

const CountryConfigEntrySchema = z.object({
  phoneNumber: PhoneNumberVariantSchema.optional().default('hidden'),
  meetingLink: MeetingLinkSchema.optional(),
  discoveryCallLink: MeetingLinkSchema.optional(),
  // Live 1:1 demo variant of the sidebar demo slot. When it resolves for the
  // user's country + language (and the account is inside the optional
  // new-user window below), it takes the slot INSTEAD of `discoveryCallLink`
  // — a user never sees both CTAs.
  liveDemoLink: MeetingLinkSchema.optional(),
  // Only accounts younger than this many days get the live-demo variant.
  // Absent = no age cap. Ineligible users fall back to `discoveryCallLink`.
  liveDemoMaxAccountAgeDays: z.number().int().positive().optional(),
  webinar: z.boolean().optional().default(true),
  onboardingCta: OnboardingCtaSchema.optional(),
});

export const CountryConfigSchema = z.record(CountryCodeSchema, CountryConfigEntrySchema);

export type CountryConfigEntry = z.infer<typeof CountryConfigEntrySchema>;
export type CountryConfig = z.infer<typeof CountryConfigSchema>;
export type PhoneNumberVariant = z.infer<typeof PhoneNumberVariantSchema>;
export type CtaSelector = z.infer<typeof CtaSelectorSchema>;
export type CtaConfig = z.infer<typeof CtaConfigSchema>;
